import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  LocalShipping as CargoIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Public as PublicIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSCargoSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ats-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to ATS System"
        >
          Back to ATS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            ATS-Cargo (ATS-C)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Cargo Targeting and Risk Assessment Subsystem
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subsystem Overview
          </Typography>
          <Typography variant="body1" paragraph>
            ATS-Cargo (ATS-C) is the primary cargo targeting subsystem within the Automated Targeting System, designed 
            to identify high-risk inbound and outbound shipments before they arrive at or depart from U.S. ports. Using 
            advanced analytics, machine learning algorithms, and rule-based targeting, ATS-C processes electronic bills 
            of lading, cargo manifests, and export declarations to generate risk scores for millions of shipments annually, 
            enabling CBP officers to focus inspection resources on the highest-risk cargo.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Subsystem Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS-Cargo 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS-Cargo"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>cargo risk assessment and targeting platform</strong> that analyzes inbound and outbound 
                    shipments using <strong>electronic manifest data, bills of lading, and export declarations</strong>. 
                    Employs <strong>machine learning models, rule-based targeting, and anomaly detection</strong> to identify 
                    shipments with potential threats including <strong>contraband, weapons, narcotics, counterfeit goods, 
                    and trade violations</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ATS-Cargo"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Used by <strong>CBP National Targeting Center (NTC) analysts</strong>, <strong>port-level cargo 
                    targeting officers</strong>, <strong>import specialists</strong>, and <strong>field officers at 
                    seaports, airports, and land borders</strong>. Also supports <strong>ICE Homeland Security Investigations 
                    (HSI)</strong>, <strong>Coast Guard</strong>, and other partner agencies for cargo interdiction operations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS-Cargo deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial deployment in <strong>early 2000s</strong> following 9/11 security enhancements. Major upgrades 
                    in <strong>2006 (SAFE Port Act compliance)</strong>, <strong>2012 (advanced analytics)</strong>, and 
                    <strong>2018-2020 (machine learning integration)</strong>. Operates <strong>24/7/365</strong> with 
                    real-time processing of manifest data submitted <strong>24 hours before vessel arrival</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS-Cargo deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>National Targeting Center (NTC)</strong> in Sterling, VA for centralized analysis. Deployed at 
                    <strong> 328 ports of entry</strong> including major seaports (Los Angeles, Long Beach, New York/Newark, 
                    Savannah, Houston), <strong>international airports</strong>, and <strong>land border crossings</strong>. 
                    Integrated with <strong>Automated Commercial Environment (ACE)</strong> for trade processing.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS-Cargo exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>advance risk assessment of cargo</strong> before arrival at U.S. borders, enabling 
                    <strong> proactive interdiction of threats</strong> while facilitating legitimate trade. Critical for 
                    <strong> national security, trade enforcement, and supply chain protection</strong>. Processes over 
                    <strong> 30 million cargo shipments annually</strong>, identifying high-risk shipments for examination 
                    while expediting low-risk cargo.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Targeting Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #003366' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Risk Scoring & Analytics
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Advanced algorithms generate risk scores for every shipment.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Machine Learning Models:</strong> Predictive risk scoring based on historical patterns</Typography></li>
                <li><Typography variant="body2"><strong>Rule-Based Targeting:</strong> Expert-defined rules for known threat indicators</Typography></li>
                <li><Typography variant="body2"><strong>Anomaly Detection:</strong> Identifies unusual shipping patterns and behaviors</Typography></li>
                <li><Typography variant="body2"><strong>Entity Risk Profiling:</strong> Shipper, consignee, and carrier risk assessment</Typography></li>
                <li><Typography variant="body2"><strong>Commodity Analysis:</strong> High-risk goods identification (weapons, drugs, counterfeits)</Typography></li>
                <li><Typography variant="body2"><strong>Trade Pattern Analysis:</strong> Detects trade fraud and revenue loss schemes</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Data Integration & Processing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Consolidates data from multiple sources for comprehensive analysis.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>ACE Integration:</strong> Automated Commercial Environment manifest data</Typography></li>
                <li><Typography variant="body2"><strong>Bill of Lading:</strong> Electronic shipping documentation processing</Typography></li>
                <li><Typography variant="body2"><strong>AES Data:</strong> Automated Export System declarations</Typography></li>
                <li><Typography variant="body2"><strong>Carrier Data:</strong> Vessel, aircraft, and truck tracking information</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Feeds:</strong> Law enforcement and intelligence data integration</Typography></li>
                <li><Typography variant="body2"><strong>Historical Data:</strong> Past seizures, violations, and enforcement actions</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Threat Detection
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Identifies multiple threat categories across cargo shipments.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Terrorism Threats:</strong> Weapons, explosives, WMD components</Typography></li>
                <li><Typography variant="body2"><strong>Narcotics Smuggling:</strong> Cocaine, heroin, fentanyl, methamphetamine</Typography></li>
                <li><Typography variant="body2"><strong>Contraband:</strong> Prohibited goods, endangered species, hazardous materials</Typography></li>
                <li><Typography variant="body2"><strong>Counterfeit Goods:</strong> Intellectual property violations, fake products</Typography></li>
                <li><Typography variant="body2"><strong>Trade Violations:</strong> Undervaluation, misclassification, quota evasion</Typography></li>
                <li><Typography variant="body2"><strong>Sanctions Violations:</strong> Prohibited countries, entities, and individuals</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CargoIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Examination & Enforcement
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Supports field operations and enforcement actions.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Exam Recommendations:</strong> Prioritized list of high-risk shipments</Typography></li>
                <li><Typography variant="body2"><strong>NII Targeting:</strong> Non-intrusive inspection (X-ray) selection</Typography></li>
                <li><Typography variant="body2"><strong>Physical Exam Guidance:</strong> Targeting notes for field officers</Typography></li>
                <li><Typography variant="body2"><strong>Hold Authority:</strong> Shipment detention for further review</Typography></li>
                <li><Typography variant="body2"><strong>Seizure Documentation:</strong> Enforcement action tracking and reporting</Typography></li>
                <li><Typography variant="body2"><strong>Feedback Loop:</strong> Exam results inform future targeting models</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cargo Types Processed */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CargoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Cargo Types & Modalities
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Cargo Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Transport Mode</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Annual Volume</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Key Risk Factors</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Ocean Containerized</strong></TableCell>
              <TableCell>Vessel (TEU containers)</TableCell>
              <TableCell>~12 million containers</TableCell>
              <TableCell>Concealment, transshipment, entity risk</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Air Cargo</strong></TableCell>
              <TableCell>Aircraft (express/freight)</TableCell>
              <TableCell>~5 million shipments</TableCell>
              <TableCell>High-value goods, time-sensitive contraband</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Rail Cargo</strong></TableCell>
              <TableCell>Rail (intermodal)</TableCell>
              <TableCell>~8 million railcars</TableCell>
              <TableCell>Bulk commodities, cross-border trade</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Truck Cargo</strong></TableCell>
              <TableCell>Commercial vehicles</TableCell>
              <TableCell>~11 million trucks</TableCell>
              <TableCell>Land border smuggling, USMCA violations</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Break Bulk</strong></TableCell>
              <TableCell>Vessel (non-containerized)</TableCell>
              <TableCell>~500K shipments</TableCell>
              <TableCell>Oversized cargo, vehicle imports</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Mail/Express</strong></TableCell>
              <TableCell>USPS, courier services</TableCell>
              <TableCell>~600 million packages</TableCell>
              <TableCell>E-commerce, small parcel smuggling</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #003366' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                30M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Shipments
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Cargo entries processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                500K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Exams Annually
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Targeted inspections
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Real-time processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                328
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ports of Entry
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Nationwide deployment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Advanced Screening:</strong> Risk assessment before cargo arrival</Typography></li>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Focus inspections on highest-risk shipments</Typography></li>
                <li><Typography variant="body2"><strong>Trade Facilitation:</strong> Expedite low-risk cargo clearance</Typography></li>
                <li><Typography variant="body2"><strong>Threat Interdiction:</strong> Proactive detection of contraband and violations</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Revenue Protection:</strong> Identify trade fraud and duty evasion</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Integration:</strong> Leverage multi-agency data sources</Typography></li>
                <li><Typography variant="body2"><strong>Supply Chain Security:</strong> Protect U.S. commerce from threats</Typography></li>
                <li><Typography variant="body2"><strong>Data-Driven Decisions:</strong> Evidence-based targeting and enforcement</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Key System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="ACE (Trade Processing)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Automated Commercial Environment</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="TECS (Law Enforcement)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Treasury Enforcement System</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="AES (Export Data)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Automated Export System</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="NII Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Non-Intrusive Inspection</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="AIS/VMS Tracking" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Vessel tracking systems</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Intelligence Databases" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">FBI, DEA, ICE data feeds</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Trade Data Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Commercial invoices, permits</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Partner Systems" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Foreign customs agencies</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSCargoSystemDetail;
